import styled from 'styled-components';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { colors } from '@config/seafoam_colors';

export const BlogContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: ${spacing['4xl']} ${spacing.xl} ${spacing['5xl']};
`;

export const HeroSection = styled.header`
  text-align: center;
  margin-bottom: ${spacing['3xl']};
`;

export const Tag = styled.span`
  display: inline-block;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeights.medium};
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${colors.accent};
  background: rgba(61, 148, 132, 0.1);
  border: 1px solid rgba(61, 148, 132, 0.2);
  padding: 6px 18px;
  border-radius: 100px;
  margin-bottom: ${spacing.xl};
`;

export const Title = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['4xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.lg} 0;
  line-height: ${typography.lineHeight.tight};

  @media (max-width: 768px) {
    font-size: ${typography.fontSize['3xl']};
  }
`;

export const Subtitle = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.lg};
  color: ${colors.textSecondary};
  max-width: 560px;
  margin: 0 auto ${spacing.lg};
  line-height: ${typography.lineHeight.relaxed};
`;

export const Byline = styled.p`
  font-size: ${typography.fontSize.xs};
  color: ${colors.textMuted};
  letter-spacing: 0.04em;

  span {
    color: ${colors.accent};
  }
`;

export const ArticleBody = styled.article`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.bodyRelaxed};

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    font-weight: ${typography.fontWeights.semibold};
    color: ${colors.text};
    margin: ${spacing['3xl']} 0 ${spacing.lg} 0;
    line-height: ${typography.lineHeight.tight};
  }

  h3 {
    font-family: ${typography.fontFamily.body};
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeights.medium};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${colors.accent};
    margin: ${spacing['3xl']} 0 ${spacing.lg} 0;
  }

  p {
    margin-bottom: 1.5em;
    color: ${colors.textSecondary};
  }

  p strong {
    color: ${colors.text};
    font-weight: ${typography.fontWeights.medium};
  }
`;

export const ChartFigure = styled.figure`
  margin: ${spacing['3xl']} -40px ${spacing['3xl']};
  padding: 2px;
  border-radius: ${borderRadius.lg};
  background: linear-gradient(135deg, rgba(61, 148, 132, 0.35), rgba(148, 163, 184, 0.2));

  img {
    width: 100%;
    display: block;
    border-radius: 14px;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const ChartCaption = styled.figcaption`
  text-align: center;
  font-size: ${typography.fontSize.xs};
  color: ${colors.textMuted};
  margin-top: ${spacing.md};
  padding: 0 40px;
  font-style: italic;
`;

export const Blockquote = styled.blockquote`
  position: relative;
  margin: ${spacing['2xl']} 0;
  padding: ${spacing.xl} ${spacing['2xl']};
  background: rgba(216, 243, 237, 0.35);
  border-left: 3px solid ${colors.accent};
  border-radius: 0 ${borderRadius.md} ${borderRadius.md} 0;
  font-size: ${typography.fontSize.lg};
  color: ${colors.text};
  font-weight: ${typography.fontWeights.light};
  line-height: ${typography.lineHeight.relaxed};

  cite {
    display: block;
    font-size: ${typography.fontSize.xs};
    color: ${colors.textMuted};
    margin-top: ${spacing.md};
    font-style: normal;
    letter-spacing: 0.02em;
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing.lg};
  margin: ${spacing['2xl']} 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  background: rgba(216, 243, 237, 0.35);
  border: 1px solid rgba(184, 230, 219, 0.5);
  border-radius: ${borderRadius.md};
  padding: ${spacing.xl} ${spacing.lg};
  text-align: center;
`;

export const StatNumber = styled.div<{ $color?: 'green' | 'coral' | 'lavender' }>`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['3xl']};
  line-height: 1;
  margin-bottom: 6px;
  color: ${({ $color }) => {
    switch ($color) {
      case 'green': return colors.accent;
      case 'coral': return '#DC2626';
      case 'lavender': return '#7C3AED';
      default: return colors.text;
    }
  }};
`;

export const StatLabel = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.textMuted};
  font-weight: ${typography.fontWeights.regular};
  letter-spacing: 0.02em;
  line-height: 1.4;
`;

export const Timeline = styled.div`
  position: relative;
  margin: ${spacing['2xl']} 0;
  padding-left: 28px;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: linear-gradient(to bottom, ${colors.accent}, #DC2626);
    border-radius: 1px;
  }
`;

export const TimelineItem = styled.div`
  position: relative;
  margin-bottom: ${spacing.xl};

  &::before {
    content: '';
    position: absolute;
    left: -25px;
    top: 8px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${colors.background};
    border: 2px solid ${colors.accent};
  }
`;

export const TimelineDate = styled.div`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeights.medium};
  color: #7C3AED;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

export const TimelineText = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.normal};

  strong {
    color: ${colors.text};
    font-weight: ${typography.fontWeights.medium};
  }
`;

export const Divider = styled.hr`
  height: 1px;
  border: none;
  background: linear-gradient(to right, transparent, rgba(148, 163, 184, 0.4), transparent);
  margin: ${spacing['3xl']} 0;
`;
